import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * WEBSITE STUDIO
 *
 * The structures the site builder is made of and nothing else shares: the
 * three-pane editor frame, the rail row that names a page, the device frames
 * the preview sits in, the address bar above them, and the "open this on a
 * desktop" screen. Each was ~100 lines of hand-written markup inside a page;
 * here they are drawn from tokens so a radius or a shadow change reaches them.
 */

/**
 * The card the whole editor sits in: fills the viewport under the shell
 * header, clips its panes, and is the positioning context for anything laid
 * over it (a loading sweep, a confetti burst).
 */
export const studioFrameRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'relative flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm',
  ],
  {
    variants: {
      height: {
        /** The live editor and the desktop-only gate. */
        default: 'h-[calc(100vh-6rem)] min-h-[520px] md:h-[calc(100vh-8rem)]',
        /** The loading skeleton, which sits where no page header does. */
        tall: 'h-[calc(100vh-4rem)] min-h-[640px]',
      },
    },
    defaultVariants: { height: 'default' },
  }
);

/** The top bar: brand on the left, save state and publish on the right. */
export const studioHeaderRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex h-14 shrink-0 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur',
]);

/**
 * rail | stage | inspector. One column below `lg`, where the gate replaces
 * the editor anyway; the side panes hide there so the stage keeps the room.
 */
export const studioBodyRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[256px_1fr_344px] xl:grid-cols-[280px_1fr_372px]',
]);

export const studioPaneRecipe = cva([uiMarker, uiMarkerLayout, 'min-h-0'], {
  variants: {
    pane: {
      rail: 'hidden border-r border-border lg:block',
      stage: 'min-w-0 bg-muted/30',
      inspector: 'hidden border-l border-border lg:block',
    },
  },
});

/** A note under the panes — the small-screen hint on the skeleton. */
export const studioFooterRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'border-t border-border bg-muted/40 px-4 py-2 lg:hidden',
]);

export type StudioFrameRecipeProps = VariantProps<typeof studioFrameRecipe>;
export type StudioPaneRecipeProps = VariantProps<typeof studioPaneRecipe>;

/**
 * PANE
 *
 * What goes inside each of StudioLayout's three slots: a column that takes
 * the pane's full height, a fixed-height header across the top, and a body
 * that scrolls under it. The rail, the inspector, the booking editor's copies
 * of both and the loading skeleton each drew this — six headers at `h-12
 * shrink-0 px-4`, four bodies at `min-h-0 overflow-y-auto` plus a padding
 * each had picked on its own.
 */
export const paneRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex h-full min-h-0 flex-col',
]);

export const paneHeaderRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex shrink-0 items-center gap-3 px-4'],
  {
    variants: {
      /** The rail and inspector headers are 48px; the stage's eyebrow bar 36px. */
      size: { sm: 'h-9', md: 'h-12' },
      justify: { start: 'justify-start', between: 'justify-between' },
      /** A rule under it, where the body scrolls past. */
      divided: { true: 'border-b border-border', false: '' },
    },
    defaultVariants: { size: 'md', justify: 'start', divided: false },
  }
);

export const paneBodyRecipe = cva(
  [uiMarker, uiMarkerLayout, 'min-h-0 flex-1'],
  {
    variants: {
      /** Which pane it fills; each has its own inset and scroll behaviour. */
      pane: {
        /** Rows carry their own padding, so the inset is tight and the bottom breathes. */
        rail: 'overflow-y-auto px-2 pb-4',
        /** The preview canvas: clips rather than scrolls, and centres the frame. */
        stage: 'relative flex items-start justify-center overflow-hidden pt-1',
        /** Form fields, on the card's own inset. */
        inspector: 'overflow-y-auto p-4',
      },
    },
    defaultVariants: { pane: 'inspector' },
  }
);

export type PaneHeaderRecipeProps = VariantProps<typeof paneHeaderRecipe>;
export type PaneBodyRecipeProps = VariantProps<typeof paneBodyRecipe>;

/**
 * RAIL ROW
 *
 * One page in the studio's left rail (and one panel in the booking editor's):
 * an icon tile, a name over a hint, and whatever acts on it at the right.
 * `selected` tints the row and pins a bar to its left edge; `loading` draws
 * the same shape out of Skeleton blocks so the swap to real rows is a fill
 * rather than a jump.
 */
export const railRowRecipe = cva(
  [
    uiMarker,
    'group relative flex w-full select-none items-center gap-3 overflow-hidden rounded-xl border px-2 py-2.5 text-left transition-all duration-200',
  ],
  {
    variants: {
      selected: {
        true: 'border-primary/30 bg-gradient-to-r from-primary/[0.12] to-transparent shadow-sm',
        false: 'border-transparent',
      },
      interactive: {
        true: 'cursor-pointer',
        false: '',
      },
      loading: {
        true: '',
        false: '',
      },
      /** A page that is hidden from the site: still there, reads switched off. */
      dimmed: {
        true: 'opacity-55',
        false: '',
      },
    },
    compoundVariants: [
      {
        selected: false,
        interactive: true,
        class: 'hover:border-border hover:bg-muted/60',
      },
      // The skeleton's tint is a shade lighter and casts no shadow: it is a
      // placeholder for a selection, not one.
      {
        selected: true,
        loading: true,
        class: 'from-primary/[0.1] shadow-none',
      },
    ],
    defaultVariants: {
      selected: false,
      interactive: false,
      loading: false,
      dimmed: false,
    },
  }
);

/** The bar on the left edge that says "this one". */
export const railRowBarRecipe = cva(
  [uiMarker, 'absolute inset-y-2 left-0 w-1 rounded-r-full'],
  {
    variants: {
      loading: { true: 'bg-primary/40', false: 'bg-primary' },
    },
    defaultVariants: { loading: false },
  }
);

export const railRowTileRecipe = cva(
  [
    uiMarker,
    'flex size-9 shrink-0 items-center justify-center rounded-lg transition-all',
  ],
  {
    variants: {
      selected: {
        true: 'bg-primary text-primary-foreground shadow-[0_1px_2px_0_hsl(var(--primary)/0.3)]',
        false:
          'bg-background text-muted-foreground ring-1 ring-border group-hover:text-foreground',
      },
    },
    defaultVariants: { selected: false },
  }
);

export type RailRowRecipeProps = VariantProps<typeof railRowRecipe>;

/**
 * DRAG HANDLE
 *
 * The grip that starts a reorder. The pointer handler comes from the caller —
 * framer's `dragControls.start` in the studio — because the library owns
 * how a handle looks, not which drag system moves the row.
 */
export const dragHandleRecipe = cva(
  [
    uiMarker,
    'flex w-5 shrink-0 cursor-grab items-center justify-center text-muted-foreground/40 active:cursor-grabbing',
  ],
  {
    variants: {
      size: { sm: 'h-6', md: 'h-8' },
      /** Hidden until the row is hovered. Wants `group` on the row. */
      reveal: {
        true: 'opacity-0 transition-opacity group-hover:opacity-100',
        false: 'transition-colors hover:text-muted-foreground',
      },
    },
    defaultVariants: { size: 'sm', reveal: false },
  }
);

/** A row that cannot be reordered shows a lock where the grip would be. */
export const dragLockRecipe = cva([
  uiMarker,
  'flex h-6 w-5 shrink-0 items-center justify-center text-muted-foreground/40',
]);

export type DragHandleRecipeProps = VariantProps<typeof dragHandleRecipe>;

/**
 * ADDRESS BAR
 *
 * The pill above the preview that names where the site lives. Lifts a hair on
 * hover like a real browser's, and blurs the address for an owner who cannot
 * publish yet — the link is not theirs to share.
 */
export const addressBarRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex shrink-0 items-stretch justify-center px-4 pb-1.5 pt-0.5',
]);

export const addressBarPillRecipe = cva([
  uiMarker,
  'group flex w-full max-w-md items-center gap-3 rounded-full border border-border bg-background py-1.5 pl-2.5 pr-1.5 shadow-sm',
  'transition-all duration-200 hover:-translate-y-px hover:border-primary/30 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0',
]);

export const addressBarAddressRecipe = cva(
  [uiMarker, 'truncate font-sans text-body-strong'],
  {
    variants: {
      live: { true: 'text-foreground', false: 'text-muted-foreground' },
      blurred: { true: 'select-none blur-[5px]', false: '' },
    },
    defaultVariants: { live: false, blurred: false },
  }
);

export type AddressBarAddressRecipeProps = VariantProps<
  typeof addressBarAddressRecipe
>;

/** One address in the "your addresses" menu: a status dot before two lines. */
export const addressMenuRowRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex w-full items-start gap-2',
]);

export const addressMenuDotRecipe = cva(
  [uiMarker, 'mt-[7px] size-1.5 shrink-0 rounded-full'],
  {
    variants: {
      tone: {
        success: 'bg-success',
        warning: 'bg-warning',
        info: 'bg-info',
        neutral: 'bg-muted-foreground',
      },
    },
    defaultVariants: { tone: 'neutral' },
  }
);

export type AddressMenuDotRecipeProps = VariantProps<
  typeof addressMenuDotRecipe
>;

/**
 * DEVICE FRAMES
 *
 * What the preview sits inside. The browser is a bordered card with a chrome
 * bar; the phone is a black bezel around a rounded screen. Both take their
 * pixel size from the caller, because the stage scales them to fit.
 */
export const browserFrameRecipe = cva([
  uiMarker,
  'relative flex flex-col overflow-hidden rounded-xl border border-border bg-background shadow-e3',
]);

export const browserChromeRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex shrink-0 items-center gap-3'],
  {
    variants: {
      size: {
        /** The full-size preview frame. */
        md: 'border-b border-border bg-muted px-4',
        /** The favicon preview's toolbar. */
        sm: 'gap-2 border-t border-border bg-background px-2.5 py-1.5',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

/** The three window buttons. Tokens stand in for the macOS colours. */
export const trafficLightsRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex items-center'],
  {
    variants: {
      size: { sm: 'gap-1', md: 'gap-2' },
    },
    defaultVariants: { size: 'md' },
  }
);

export const trafficLightRecipe = cva([uiMarker, 'rounded-full'], {
  variants: {
    size: { sm: 'size-2.5', md: 'size-3.5' },
    tone: {
      close: 'bg-destructive',
      minimise: 'bg-warning',
      zoom: 'bg-success',
    },
  },
  defaultVariants: { size: 'md', tone: 'close' },
});

export const browserAddressRecipe = cva(
  [uiMarker, 'flex min-w-0 items-center gap-2 text-muted-foreground'],
  {
    variants: {
      size: {
        md: 'w-full max-w-lg rounded-lg bg-background px-3 py-1.5 text-[13px] shadow-inner',
        sm: 'flex-1 truncate rounded-md bg-muted px-2 py-1 text-[10px]',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

/** The bezel is `bg-foreground`: a dark device in light mode, a light one in dark. */
export const phoneFrameRecipe = cva([
  uiMarker,
  'relative bg-foreground shadow-e3',
]);

export const phoneScreenRecipe = cva([
  uiMarker,
  'relative overflow-hidden bg-background',
]);

export type BrowserChromeRecipeProps = VariantProps<typeof browserChromeRecipe>;
export type TrafficLightRecipeProps = VariantProps<typeof trafficLightRecipe>;

/**
 * SITE FAVICON
 *
 * A site's icon at tab size, with a stand-in when there is none: the site's
 * initial, or a globe when there is not even a name. Square for a browser
 * tab, round for a search result. The checkerboard behind a square one is
 * what keeps a transparent icon visible.
 */
export const siteFaviconRecipe = cva(
  [
    uiMarker,
    'flex size-4 shrink-0 items-center justify-center overflow-hidden',
  ],
  {
    variants: {
      shape: {
        square: 'rounded-[3px]',
        circle: 'rounded-full',
      },
      fill: {
        /** Behind a real icon. */
        image: '',
        /** Behind an initial. */
        initial:
          'bg-muted font-sans text-[9px] font-semibold uppercase text-muted-foreground',
      },
    },
    compoundVariants: [
      {
        shape: 'square',
        fill: 'image',
        class:
          'bg-background [background-image:linear-gradient(45deg,theme(colors.border)_25%,transparent_25%,transparent_75%,theme(colors.border)_75%),linear-gradient(45deg,theme(colors.border)_25%,transparent_25%,transparent_75%,theme(colors.border)_75%)] [background-position:0_0,3px_3px] [background-size:6px_6px]',
      },
      {
        shape: 'circle',
        fill: 'image',
        class: 'bg-background ring-1 ring-border',
      },
      { shape: 'circle', fill: 'initial', class: 'text-[8px]' },
    ],
    defaultVariants: { shape: 'square', fill: 'image' },
  }
);

export const siteFaviconImageRecipe = cva([uiMarker, 'object-contain'], {
  variants: {
    shape: { square: 'size-4', circle: 'size-3.5' },
  },
  defaultVariants: { shape: 'square' },
});

export type SiteFaviconRecipeProps = VariantProps<typeof siteFaviconRecipe>;

/**
 * TAB PREVIEW
 *
 * How the favicon reads in a real browser: a desktop tab strip over a
 * toolbar, or a phone's address pill over a tab-switcher card.
 */
export const tabPreviewRecipe = cva([uiMarker], {
  variants: {
    variant: {
      desktop: 'overflow-hidden rounded-lg border border-border bg-muted',
      mobile:
        'flex flex-col items-center gap-2 rounded-lg border border-border bg-background p-3',
    },
  },
  defaultVariants: { variant: 'desktop' },
});

export const tabStripRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex items-end gap-1 px-2 pt-1.5',
]);

export const tabRecipe = cva([
  uiMarker,
  'flex max-w-[190px] items-center gap-2 rounded-t-lg bg-background px-2.5 py-1.5 text-[11px] text-muted-foreground shadow-e1',
]);

export const mobileAddressPillRecipe = cva([
  uiMarker,
  'flex w-full items-center gap-2 rounded-full bg-muted px-2.5 py-1.5 text-[10px] text-muted-foreground',
]);

export const tabSwitcherCardRecipe = cva([
  uiMarker,
  'w-24 overflow-hidden rounded-lg border border-border shadow-sm',
]);

export const tabSwitcherHeadRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex items-center gap-1 bg-muted px-1.5 py-1 text-[8px] text-muted-foreground',
]);

export const tabSwitcherBodyRecipe = cva([
  uiMarker,
  'block h-10 bg-gradient-to-b from-muted to-background',
]);

export type TabPreviewRecipeProps = VariantProps<typeof tabPreviewRecipe>;

/**
 * DESKTOP-ONLY NOTICE
 *
 * Shown in place of the editor on a screen too narrow to hold three panes. A
 * faint grid and a glow behind a centred column: a monitor medallion with a
 * crossed-out phone, a title, a line of explanation, a way out.
 */
export const desktopNoticeRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'relative flex min-h-0 flex-1 items-center justify-center px-6 py-10',
]);

export const desktopNoticeGridRecipe = cva([
  uiMarker,
  'pointer-events-none absolute inset-0 opacity-[0.5] [background-image:linear-gradient(to_right,theme(colors.border)_1px,transparent_1px),linear-gradient(to_bottom,theme(colors.border)_1px,transparent_1px)] [background-size:32px_32px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]',
]);

export const desktopNoticeGlowRecipe = cva([
  uiMarker,
  'pointer-events-none absolute -top-24 left-1/2 h-64 w-[28rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl',
]);

export const desktopNoticeColumnRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'relative flex w-full max-w-sm flex-col items-center text-center',
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-500 motion-safe:fill-mode-both',
]);

export const desktopMedallionRecipe = cva([uiMarker, 'relative mb-8']);

export const desktopMedallionGlowRecipe = cva([
  uiMarker,
  'absolute inset-0 -z-10 rounded-[1.75rem] bg-primary/20 blur-2xl',
]);

export const desktopMedallionDiscRecipe = cva([
  uiMarker,
  'flex size-24 items-center justify-center rounded-[1.75rem] bg-primary/10 text-primary shadow-inner ring-1 ring-inset ring-primary/15',
]);

export const desktopMedallionBadgeRecipe = cva([
  uiMarker,
  'absolute -bottom-2 -right-2 flex size-10 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-md',
]);

export const desktopMedallionStrikeRecipe = cva([
  uiMarker,
  'absolute h-[1.5px] w-6 rotate-45 rounded-full bg-muted-foreground/80',
]);
